import { CategoryData, ServiceItem } from "@/types";
import { Check } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { formatDuration, formatPrice } from "@/utils/serviceFormatters";

// Tronquer la description à 80 caractères pour mobile
const truncateDescription = (text: string, maxLength: number = 80) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + "...";
};

// Calculer le total de durée en minutes
const calculateTotalDuration = (services: ServiceItem[]) => {
  const totalMinutes = services.reduce((sum, service) => {
    const match = service.duration.toString().match(/(\d+)/);
    return sum + (match ? parseInt(match[1], 10) : 0);
  }, 0);
  return formatDuration(totalMinutes.toString());
};

// Calculer le total des prix
const calculateTotalPrice = (services: ServiceItem[]) => {
  const total = services.reduce((sum, service) => {
    const match = service.price.match(/[\d.,]+/);
    return sum + (match ? parseFloat(match[0].replace(",", ".")) : 0);
  }, 0);
  return formatPrice(total);
};

interface ServiceSelectionProps {
  categories: CategoryData[];
  selectedServices: ServiceItem[];
  onSelectService: (service: ServiceItem) => void;
  isLoading: boolean;
}

const ServiceSelection = ({ categories, selectedServices, onSelectService, isLoading }: ServiceSelectionProps) => {
  const isSelected = (serviceId: string) => {
    return selectedServices.some((service) => service.id === serviceId);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-3/4" />
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="text-center p-6">
        <p className="text-muted-foreground">Aucun service n'est disponible pour le moment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="pb-4">
        <Accordion type="multiple" className="space-y-2">
          {categories.map((category) => (
            <AccordionItem
              key={category.id}
              value={category.id}
              className="border border-bbz-green/20 rounded-md overflow-hidden"
            >
              <AccordionTrigger className="px-4 py-2 hover:bg-bbz-beige/10 hover:no-underline">
                <span className="font-medium text-lg">{category.title}</span>
                {category.services.some((service) => isSelected(service.id)) && (
                  <span className="ml-2 text-xs bg-bbz-green/20 px-2 py-1 rounded-full">
                    {category.services.filter((service) => isSelected(service.id)).length} sélectionné(s)
                  </span>
                )}
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-2">
                <div className="space-y-2">
                  {category.services.map((service) => (
                    <div
                      key={service.id}
                      onClick={() => onSelectService(service)}
                      className={`p-3 rounded-md border cursor-pointer transition-all relative ${
                        isSelected(service.id)
                          ? "border-bbz-green bg-bbz-green/30 border-l-4 border-l-bbz-green shadow-sm"
                          : "border-bbz-green/20 hover:border-bbz-green/50 hover:bg-bbz-beige/30"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-sm">{service.name}</h4>
                            {isSelected(service.id) && (
                              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-bbz-green flex items-center justify-center">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-sm font-medium text-bbz-text/80">
                              {formatDuration(service.duration)}
                            </span>
                            <span className="text-bbz-text/40">•</span>
                            <span className="text-sm font-semibold text-bbz-text">{formatPrice(service.price)}</span>
                          </div>
                          {service.description && (
                            <p className="text-xs mt-1.5 text-muted-foreground leading-relaxed">
                              {truncateDescription(service.description)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {selectedServices.length > 0 && (
        <div className="bg-bbz-green/20 border border-bbz-green/30 p-3 rounded-md mt-4">
          <div className="flex items-center justify-between">
            <div className="w-full">
              <p className="text-sm font-semibold text-bbz-text">
                {selectedServices.length} service{selectedServices.length > 1 ? "s" : ""} sélectionné
                {selectedServices.length > 1 ? "s" : ""}
              </p>
              <p className="text-xs text-bbz-text/80 mt-1">
                {selectedServices.map((service) => service.name).join(", ")}
              </p>
              <p className="text-xs text-bbz-text/70 mt-0.5">
                Total : {calculateTotalDuration(selectedServices)} • {calculateTotalPrice(selectedServices)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceSelection;
